import { useTranslations } from "next-intl";
import { Skeleton } from "@radix-ui/themes";
import { DataInputParams, PickerType } from "./inputType";
import { Form, Input, Skeleton as AntSkeleton, DatePicker, ConfigProvider, TimePicker, DatePickerProps } from "antd";
import { Debug } from "@/util/debug";
import { GetDataInputElement, getDatePicker, getMultiValue } from "./inputFunction";
import SkeletonInput from "antd/es/skeleton/Input";
import { getParamsValue } from "@/util/paramutil";
import { useContext } from "react";
import { TransitionType } from "@/components/layout/layoutType";
import dayjs, { locale } from "dayjs";
import { arrPickObj, getStringArr } from "@/util/arrayutil";
const getDayjs = (value: any): dayjs.Dayjs | dayjs.Dayjs[] => {
  if (typeof value == "string") {
    if (value.indexOf(",") > 0) {
      const dv = value.split(",").map((i) => {
        return dayjs(i);
      });
      return dv;
    } else {
      return dayjs(value);
    }
  } else if (value instanceof Array) {
    const dv = value.map((i) => {
      return dayjs(i);
    });
    return dv;
  } else {
    return dayjs(value);
  }
};
const getDayjsStr = (value: dayjs.Dayjs | dayjs.Dayjs[] | undefined, formatter?: string) => {
  if (value === undefined) {
    return undefined;
  } else if (value instanceof Array) {
    return value
      .map((i) => {
        return i.format(formatter);
      })
      .join(" ");
  } else {
    return value.format(formatter);
  }
};
const getDayjsRange = (
  value: dayjs.Dayjs | dayjs.Dayjs[] | undefined,
  formatter?: string,
): [start: dayjs.Dayjs | null | undefined, end: dayjs.Dayjs | null | undefined] | undefined => {
  if (value === undefined) {
    return undefined;
  } else if (value instanceof Array) {
    if (value.length > 1) {
      return [value[0], value[value.length]];
    } else {
      return [value[0], null];
    }
  } else {
    return [value, null];
  }
};

const GetInput = ({ t, params, pickerType }: { t: TransitionType; params: DataInputParams; pickerType: PickerType }) => {
  // const fieldList = getStringArr(params.fieldList);
  // const values = fieldList
  //   ? fieldList.map((field) => {
  //       return params.values[field];
  //     })
  //   : params.value;
  const values = getMultiValue(params, params.value);
  const valueDate = values ? getDayjs(values) : undefined;
  const defaultDate = params.defaultValue ? getDayjs(params.defaultValue) : undefined;
  const placeholder =
    params.placeholder ??
    getDayjsStr(defaultDate, typeof params.dateFormatter === "string" ? params.dateFormatter : undefined) ??
    getDayjsStr(valueDate, typeof params.dateFormatter === "string" ? params.dateFormatter : undefined);
  const disabled = params.inputMode == "view" && (params.readInputDisabled ?? true) ? true : params.disabled;
  //const value = params.inputMode == "view" && !(params.readInputDisabled ?? false) ? valueDate : undefined;
  const PickerProp = pickerType.timePicker ? { inputReadOnly: true } : { inputReadOnly: true, picker: pickerType.picker };
  const setDateFieldValue = (date: any) => {
    if (params.form) {
      params.form.setFieldValue(params.name, date);
    }
    if (params.onChange) {
      params.onChange(date);
    }
  };
  if (pickerType.range) {
    const onChange: DatePickerProps<[start: dayjs.Dayjs | null, end: dayjs.Dayjs | null] | null>["onChange"] = (date, dateString) => {
      setDateFieldValue(date);
    };
    const Picker = pickerType.timePicker ? TimePicker : DatePicker;
    return (
      <Picker.RangePicker
        placeholder={[placeholder ?? "", ""]}
        defaultValue={valueDate ? getDayjsRange(valueDate) : getDayjsRange(defaultDate)}
        //defaultValue={[valueDate ?? defaultDate, undefined]}
        disabled={disabled}
        // id={params.name}
        // name={params.name}
        showTime={pickerType.showTime}
        mode={[pickerType.mode ?? "date", pickerType.mode ?? "date"]}
        onChange={onChange}
        {...PickerProp}
      />
    );
  } else {
    //const [value, setValue] = useState<dayjs.Dayjs | dayjs.Dayjs[] | undefined>(valueDate);
    const onChange: DatePickerProps<dayjs.Dayjs | dayjs.Dayjs[]>["onChange"] = (date, dateString) => {
      setDateFieldValue(date);
    };
    if (pickerType.timePicker) {
      return (
        <TimePicker
          placeholder={placeholder}
          //value={value}
          defaultValue={arrPickObj(valueDate) ?? arrPickObj(defaultDate)}
          disabled={disabled}
          // id={params.name}
          // name={params.name}
          mode={pickerType.mode}
          onChange={onChange}
          {...PickerProp}
        />
      );
    } else {
      return (
        <DatePicker
          placeholder={placeholder}
          //value={value}
          multiple={pickerType.multiple}
          defaultValue={valueDate ?? defaultDate}
          disabled={disabled}
          // id={params.name}
          // name={params.name}
          showTime={pickerType.showTime}
          mode={pickerType.mode}
          onChange={onChange}
          {...PickerProp}
        />
      );
    }
  }
};

export default function DataInput(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const unitPrev = params.unitPrev ? GetDataInputElement(params.unitPrev, false, params.values, t) : undefined;
  const unit = params.unit ? GetDataInputElement(params.unit, true, params.values, t) : undefined;
  const initialValue = params.value ?? params.defaultValue;
  const antdContext = useContext(ConfigProvider.ConfigContext);
  const formatter = params.dateFormatter ?? antdContext.locale?.DatePicker?.dateTimeFormat;
  const pickerType = getDatePicker(params.inputType);
  const momentValue = initialValue ? dayjs(initialValue) : undefined;
  return (
    <Skeleton loading={params.loading ?? false}>
      {prev}
      {unitPrev}
      {(params.loading ?? false) ? (
        <AntSkeleton.Input active={true} block={true} />
      ) : params.inputMode == "view" ? (
        <GetInput t={t} params={{ ...params, dateFormatter: formatter }} pickerType={pickerType} />
      ) : (
        <Form.Item name={params.name} noStyle initialValue={momentValue}>
          <GetInput t={t} params={{ ...params, dateFormatter: formatter }} pickerType={pickerType} />
        </Form.Item>
      )}
      {unit}
      {next}
    </Skeleton>
  );
}
